import Phaser from 'phaser';
import type { Creature, Stage } from '@/domain/creature/types';
import type { FoodId } from '@/domain/care/foods';
import type { ToyId } from '@/domain/care/toys';
import { deriveVisualCareAccents } from '@/domain/care/life-outcome';
import {
  deriveVisualEvolutionProfile,
  type VisualEvolutionProfile,
  type VisualLimbStyle,
} from '@/domain/care/visual-evolution';
import type { BodyGene, EarGene, HornGene, MarkingGene, TailGene } from '@/domain/genetics/types';
import { paletteFromHue } from '@/domain/genetics/palette';
import { statsToExpression, type Expression } from './animations';
import { isSleeping } from '@/domain/sleep';
import { PixelCanvas } from './pixel/PixelCanvas';
import { eventBus } from '@/infra/event-bus/EventBus';
import { STAGE_DURATIONS } from '@/domain/lifecycle';
import { resolveDeformAmplitudeForCreature, type CareTier } from '@/domain/care/evolution';
import { deriveTemperament, hasTemperament, type TemperamentProfile } from '@/domain/care/temperament';

const EGG_DURATION_MS = STAGE_DURATIONS.egg;

/**
 * Pixel-art creature renderer.
 * Trabalha em um canvas lógico de 32×32 pixels; container scale 6× = 192×192 visíveis.
 * Sem antialiasing (Phaser pixelArt:true global).
 */

export interface CreatureRendererHandle {
  container: Phaser.GameObjects.Container;
  setWorldDepth(depth: number): void;
  setFacing(direction: -1 | 1): void;
  setWalking(walking: boolean): void;
  setStagePosition(x: number, y: number): void;
  setLightOn(lightOn: boolean): void;
  setPresentationPose(pose: Partial<PresentationPose>): void;
  resetPresentationPose(): void;
  update(c: Creature): void;
  reactFeed(foodId?: FoodId): void;
  reactPlay(toyId?: ToyId): void;
  reactClean(): void;
  reactHeal(): void;
  reactNeed(kind: 'feed' | 'play' | 'clean' | 'heal'): void;
  reactRefuse(severity?: 'mild' | 'tantrum', context?: RefusalReactionContext): void;
  reactDiscipline(correct?: boolean): void;
  reactEvolve(tier: CareTier): void;
  destroy(): void;
}

interface BuildOpts {
  expressionOverride?: Expression;
  lightOn?: boolean;
  onReactionEnd?: () => void;
  onReactionStart?: () => void;
  reducedMotion?: boolean;
  scale?: number;
  showLabels?: boolean;
  sleepMode?: RendererSleepMode;
  poseOverride?: RendererPoseOverride;
}

export interface PresentationPose {
  x: number;
  y: number;
  angle: number;
  scaleX: number;
  scaleY: number;
}

export function normalizePresentationPose(
  pose: Partial<PresentationPose> = {},
): PresentationPose {
  return {
    x: pose.x ?? 0,
    y: pose.y ?? 0,
    angle: pose.angle ?? 0,
    scaleX: pose.scaleX ?? 1,
    scaleY: pose.scaleY ?? 1,
  };
}

export type RefusalReason = 'cooldown' | 'not-needed' | 'tantrum';
export interface RefusalReactionContext {
  action?: 'feed' | 'play' | 'clean' | 'discipline' | 'heal';
  reason?: RefusalReason;
}

const PIXEL_SCALE = 6; // container scale
const CANVAS = 32;     // pixels lógicos

export type RendererSleepMode = 'clock' | 'awake';
export type RendererPoseOverride = 'breathe-in' | 'breathe-out';
export type IdleBreathPhase = 'inhale' | 'exhale';
export type WalkMotionPhase = 'left' | 'right';

export interface CreatureShadowProfile {
  rx: number;
  ry: number;
  alpha: number;
  offsetX: number;
  offsetY: number;
}

export interface RendererBodyPose {
  y: number;
  scaleX: number;
  scaleY: number;
}

export interface RendererWalkPose extends RendererBodyPose {
  x: number;
}

export function idleDurationForTemperament(temperament: TemperamentProfile): number {
  if (hasTemperament(temperament, 'playful')) return 940;
  if (hasTemperament(temperament, 'calm')) return 1320;
  return 1100;
}

export function idleBreathPose(phase: IdleBreathPhase): RendererBodyPose {
  if (phase === 'inhale') return { y: 0, scaleX: 0.985, scaleY: 1.055 };
  return { y: 0.9, scaleX: 1.035, scaleY: 0.965 };
}

export function walkMotionPose(phase: WalkMotionPhase): RendererWalkPose {
  if (phase === 'left') return { x: -0.55, y: 0.45, scaleX: 1.025, scaleY: 0.98 };
  return { x: 0.55, y: 0, scaleX: 0.99, scaleY: 1.02 };
}

export function healFxScaleForTemperament(temperament: TemperamentProfile): number {
  return hasTemperament(temperament, 'fragile') ? 1.2 : 1;
}

export function refuseRepeatsForTemperament(
  temperament: TemperamentProfile,
  severity: 'mild' | 'tantrum',
): number {
  const base = severity === 'tantrum' ? 5 : 3;
  return base + (hasTemperament(temperament, 'stubborn') ? 1 : 0);
}

export function eyeOffsetForFace(_body: BodyGene, stage: 0 | 1 | 2): number {
  if (stage !== 0) return 3;
  return 2;
}

export function clampVisualDeformAmplitude(value: number): number {
  return Math.max(-1, Math.min(1, value));
}

export function stageAllowsDeepVisualEvolution(stage: Stage): boolean {
  return stage === 'teen' || stage === 'adult' || stage === 'elder';
}

export function applyVisualProfileDeform(
  baseAmplitude: number,
  profile: Pick<VisualEvolutionProfile, 'bodyBias' | 'posture' | 'deformBoost'>,
): number {
  let next = baseAmplitude + profile.deformBoost;
  if (profile.bodyBias === 'rounded' || profile.posture === 'soft' || profile.bodyBias === 'droopy') next -= 0.16;
  if (profile.bodyBias === 'sturdy' || profile.posture === 'proud') next += 0.14;
  if (profile.bodyBias === 'lean' || profile.posture === 'spiky') next -= 0.08;
  if (profile.bodyBias === 'delicate') next += 0.04;
  return clampVisualDeformAmplitude(next);
}

export function visualBodyOffset(profile: Pick<VisualEvolutionProfile, 'posture'>): { x: number; y: number } {
  if (profile.posture === 'slouched') return { x: 0, y: 1 };
  if (profile.posture === 'proud') return { x: 0, y: -1 };
  if (profile.posture === 'spiky') return { x: -1, y: 0 };
  if (profile.posture === 'bouncy') return { x: 0, y: -1 };
  if (profile.posture === 'delicate') return { x: 0, y: 0 };
  return { x: 0, y: 0 };
}

export function armLengthForVisualStyle(baseLength: number, limbStyle: VisualLimbStyle): number {
  if (limbStyle === 'tiny-paws') return Math.max(1, baseLength - 1);
  if (limbStyle === 'sturdy') return baseLength + 1;
  return baseLength;
}

export function tailGrowthForVisualStyle(baseGrowth: number, limbStyle: VisualLimbStyle): number {
  if (limbStyle === 'fins') return baseGrowth + 1;
  if (limbStyle === 'tiny-paws') return Math.max(0, baseGrowth - 1);
  return baseGrowth;
}

export function expressionForVisualBias(
  base: Expression,
  profile: Pick<VisualEvolutionProfile, 'expressionBias'>,
): Expression {
  if (base !== 'neutral') return base;
  if (profile.expressionBias === 'bright' || profile.expressionBias === 'smug') return 'happy';
  if (profile.expressionBias === 'tired') return 'sad';
  if (profile.expressionBias === 'defiant') return 'angry';
  return base;
}

export function shadowProfileForCreature(
  body: BodyGene,
  stage: 0 | 1 | 2,
  visual: Pick<VisualEvolutionProfile, 'posture' | 'limbStyle'>,
  sleeping: boolean,
): CreatureShadowProfile {
  const stageScale = [0.72, 0.92, 1.12][stage]!;
  const bodyBase: Record<BodyGene, CreatureShadowProfile> = {
    round: { rx: 6.2, ry: 1.35, alpha: 0.28, offsetX: 0, offsetY: 1 },
    tall: { rx: 4.8, ry: 1.25, alpha: 0.25, offsetX: 0, offsetY: 1 },
    blob: { rx: 7.2, ry: 1.4, alpha: 0.3, offsetX: 0, offsetY: 1 },
    fish: { rx: 8.2, ry: 1.25, alpha: 0.24, offsetX: 1, offsetY: 1 },
    ghost: { rx: 6.6, ry: 1.15, alpha: 0.16, offsetX: 0, offsetY: 0 },
  };
  const base = bodyBase[body];
  const postureWidth =
    visual.posture === 'soft' || visual.posture === 'slouched' ? 1.1 : visual.posture === 'proud' ? 0.94 : 1;
  const limbWidth = visual.limbStyle === 'sturdy' || visual.limbStyle === 'fins' ? 1.08 : 1;
  const sleepWidth = sleeping ? 1.2 : 1;
  const sleepHeight = sleeping ? 0.82 : 1;
  return {
    rx: Math.max(3, Math.round(base.rx * stageScale * postureWidth * limbWidth * sleepWidth)),
    ry: Math.max(1, Math.round(base.ry * sleepHeight)),
    alpha: Number((base.alpha * (sleeping ? 1.08 : 1)).toFixed(2)),
    offsetX: base.offsetX,
    offsetY: base.offsetY + (sleeping ? 1 : 0),
  };
}

export function resolveRendererSleeping(
  stage: Stage,
  nowMs: number,
  sleepMode: RendererSleepMode = 'clock',
): boolean {
  if (stage === 'egg' || stage === 'dead') return false;
  if (sleepMode === 'awake') return false;
  return isSleeping(nowMs);
}

export function mouthExpressionForBlink(rendered: Expression, base: Expression): Expression {
  return rendered === 'blink' ? base : rendered;
}

export interface PixelPoint {
  x: number;
  y: number;
}

export interface ColoredPixelPoint extends PixelPoint {
  color: number;
  alpha?: number;
}

export function fishMouthPixels(expr: Expression, mouthX: number, mouthY: number): PixelPoint[] {
  switch (expr) {
    case 'happy':
      return [
        { x: mouthX, y: mouthY + 1 },
        { x: mouthX + 1, y: mouthY },
      ];
    case 'sad':
      return [
        { x: mouthX, y: mouthY },
        { x: mouthX + 1, y: mouthY },
        { x: mouthX + 2, y: mouthY + 1 },
      ];
    case 'angry':
      return [
        { x: mouthX, y: mouthY },
        { x: mouthX + 1, y: mouthY },
      ];
    case 'sick':
      return [
        { x: mouthX, y: mouthY + 1 },
        { x: mouthX + 1, y: mouthY },
        { x: mouthX + 2, y: mouthY + 1 },
      ];
    case 'sleepy':
      return [];
    case 'blink':
    case 'neutral':
    default:
      return [{ x: mouthX, y: mouthY }];
  }
}

export function fishMouthAnchor(cx: number, cy: number, stage: 0 | 1 | 2): PixelPoint {
  return {
    x: cx - (stage === 0 ? 6 : 8),
    y: cy + (stage === 0 ? 2 : 1),
  };
}

export type FeedingAnimationPhase = 'present' | 'approach' | 'bite-one' | 'bite-two' | 'settle';

export interface FeedingAnimationPose {
  foodX: number;
  foodY: number;
  foodScale: number;
  bodyY: number;
}

export function feedingAnimationPose(phase: FeedingAnimationPhase): FeedingAnimationPose {
  switch (phase) {
    case 'present': return { foodX: -14, foodY: 2, foodScale: 1, bodyY: 0 };
    case 'approach': return { foodX: -7, foodY: -1, foodScale: 1, bodyY: 0 };
    case 'bite-one': return { foodX: -5, foodY: -2, foodScale: 0.75, bodyY: -1 };
    case 'bite-two': return { foodX: -3, foodY: -2, foodScale: 0.45, bodyY: -1 };
    case 'settle': return { foodX: 0, foodY: -2, foodScale: 0, bodyY: 0 };
  }
}

export function foodEffectPixels(foodId: FoodId): ColoredPixelPoint[] {
  if (foodId === 'fruit') return [
    ...rangePixels(-2, 2, 2, 0xe85a4f), ...rangePixels(-3, 3, 3, 0xe85a4f),
    ...rangePixels(-2, 2, 4, 0xc53f43), { x: 0, y: 1, color: 0x347b39 }, { x: 1, y: 0, color: 0x55b95a },
  ];
  if (foodId === 'snack') return [
    ...rangePixels(-3, 3, 2, 0xff9ac8), ...rangePixels(-2, 2, 3, 0xc53f82),
    { x: -5, y: 1, color: 0xc53f82 }, { x: -4, y: 2, color: 0xff9ac8 },
    { x: 4, y: 2, color: 0xff9ac8 }, { x: 5, y: 1, color: 0xc53f82 },
  ];
  if (foodId === 'protein') return [
    ...rangePixels(-3, 3, 2, 0xc96f38), ...rangePixels(-2, 2, 3, 0xe99a59),
    { x: -5, y: 1, color: 0xffe5bd }, { x: -4, y: 2, color: 0xffe5bd },
    { x: -5, y: 3, color: 0xffe5bd }, { x: 4, y: 2, color: 0xffe5bd },
    { x: 5, y: 1, color: 0xffe5bd }, { x: 5, y: 3, color: 0xffe5bd },
  ];
  return [
    ...rangePixels(-4, 4, 2, 0x4e9bd1), ...rangePixels(-3, 3, 3, 0x3974a0),
    ...rangePixels(-2, 2, 1, 0xf0c870), { x: 0, y: 0, color: 0xffefb5 },
  ];
}

export type ToyMotionPhase = 'present' | 'interact' | 'finish';
export interface ToyMotionPose { fxX: number; fxY: number; angle: number; scale: number; bodyY: number }

export function toyMotionPose(toyId: ToyId, phase: ToyMotionPhase): ToyMotionPose {
  if (toyId === 'ball') {
    if (phase === 'present') return { fxX: -10, fxY: 7, angle: 0, scale: 1, bodyY: 0 };
    if (phase === 'interact') return { fxX: -10, fxY: 2, angle: 90, scale: 1, bodyY: -2 };
    return { fxX: -10, fxY: 7, angle: 180, scale: 0.5, bodyY: 0 };
  }
  if (toyId === 'rope') {
    if (phase === 'present') return { fxX: 10, fxY: 4, angle: -24, scale: 1, bodyY: 0 };
    if (phase === 'interact') return { fxX: 10, fxY: 2, angle: 28, scale: 1, bodyY: -1 };
    return { fxX: 10, fxY: 4, angle: -24, scale: 0.5, bodyY: 0 };
  }
  if (phase === 'present') return { fxX: -10, fxY: 1, angle: 0, scale: 1, bodyY: 0 };
  if (phase === 'finish') return { fxX: 0, fxY: -5, angle: 0, scale: 0.5, bodyY: 0 };
  if (toyId === 'plush') return { fxX: 0, fxY: -1, angle: -8, scale: 1.15, bodyY: 0 };
  return { fxX: 2, fxY: -4, angle: 0, scale: 0.85, bodyY: -1 };
}

export function toyEffectPixels(toyId: ToyId): ColoredPixelPoint[] {
  if (toyId === 'ball') return [
    ...rangePixels(-2, 2, 1, 0x78b7ff), ...rangePixels(-3, 3, 2, 0x3974a0),
    ...rangePixels(-2, 2, 3, 0x78b7ff), { x: 1, y: 1, color: 0xffffff },
  ];
  if (toyId === 'rope') return [
    ...rangePixels(-3, 3, 0, 0xf0c870), { x: -4, y: 1, color: 0xa96b2b },
    { x: 4, y: 1, color: 0xa96b2b }, { x: -3, y: 2, color: 0xf0c870 },
    { x: 3, y: 2, color: 0xf0c870 }, { x: 0, y: 3, color: 0xf0c870 },
  ];
  if (toyId === 'plush') return [
    ...rangePixels(-2, 2, 1, 0xff9ac8), ...rangePixels(-3, 3, 2, 0xff9ac8),
    ...rangePixels(-2, 2, 3, 0xc53f82), { x: -3, y: 0, color: 0xc53f82 },
    { x: 3, y: 0, color: 0xc53f82 }, { x: -1, y: 2, color: 0x4a2440 }, { x: 1, y: 2, color: 0x4a2440 },
  ];
  return [
    ...rangePixels(-3, 3, 1, 0x58a95b), ...rangePixels(-3, 3, 2, 0x9ee68f),
    ...rangePixels(-2, 2, 3, 0x58a95b), { x: -1, y: 0, color: 0x9ee68f },
    { x: 2, y: 0, color: 0x58a95b }, { x: 0, y: 2, color: 0xf0c870 },
  ];
}

export type CleaningAnimationPhase = 'present' | 'sweep' | 'finish';
export type MedicineAnimationPhase = 'present' | 'inject' | 'recover';
export type DisciplineAnimationPhase = 'cue' | 'resolve';
export type RefusalAnimationPhase = 'cue' | 'react';

export interface CareReactionPose {
  fxX: number;
  fxY: number;
  bodyX: number;
  bodyY: number;
  angle: number;
  scale: number;
}

export function cleaningAnimationPose(phase: CleaningAnimationPhase): CareReactionPose {
  if (phase === 'present') return { fxX: 13, fxY: 7, bodyX: 0, bodyY: 0, angle: -18, scale: 1 };
  if (phase === 'sweep') return { fxX: 9, fxY: 3, bodyX: -1, bodyY: 0, angle: 22, scale: 1.05 };
  return { fxX: 12, fxY: 6, bodyX: 0, bodyY: 0, angle: 0, scale: 0.7 };
}

export function cleaningEffectPixels(): ColoredPixelPoint[] {
  return [
    ...rangePixels(-4, 3, 1, 0x7b4b2a),
    ...rangePixels(-3, 3, 2, 0xd8fbff),
    { x: 4, y: 0, color: 0x9cf6ff }, { x: 5, y: -1, color: 0xffffff },
    { x: -3, y: 3, color: 0xffffff }, { x: 1, y: -2, color: 0xd8fbff },
  ];
}

export function medicineAnimationPose(phase: MedicineAnimationPhase): CareReactionPose {
  if (phase === 'present') return { fxX: -13, fxY: 1, bodyX: 0, bodyY: 0, angle: -12, scale: 1 };
  if (phase === 'inject') return { fxX: -6, fxY: 0, bodyX: 1, bodyY: 1, angle: 8, scale: 1 };
  return { fxX: 0, fxY: -8, bodyX: 0, bodyY: 0, angle: 0, scale: 1.1 };
}

export function medicineEffectPixels(kind: 'syringe' | 'recovery'): ColoredPixelPoint[] {
  if (kind === 'recovery') return [
    ...rangePixels(-3, 3, -7, 0x66ff99), ...rangePixels(-1, 1, -9, 0xffffff),
    { x: 0, y: -11, color: 0xb8ffd0 }, { x: -7, y: -3, color: 0xb8ffd0 }, { x: 7, y: -2, color: 0xb8ffd0 },
  ];
  return [
    ...rangePixels(-5, 2, 0, 0xd8fbff), ...rangePixels(-3, 1, 1, 0x66ff99),
    { x: 3, y: 0, color: 0xc8d4df }, { x: 4, y: 0, color: 0xc8d4df },
    { x: -6, y: -1, color: 0xffffff }, { x: -6, y: 1, color: 0xffffff },
  ];
}

export function disciplineAnimationPose(correct: boolean, phase: DisciplineAnimationPhase): CareReactionPose {
  if (phase === 'cue') return { fxX: 0, fxY: -11, bodyX: 0, bodyY: 0, angle: 0, scale: 1 };
  return correct
    ? { fxX: 0, fxY: -8, bodyX: 0, bodyY: 1, angle: 0, scale: 0.97 }
    : { fxX: -5, fxY: -8, bodyX: 3, bodyY: 1, angle: 7, scale: 0.96 };
}

export function disciplineEffectPixels(_correct: boolean): ColoredPixelPoint[] {
  return [];
}

export function refusalAnimationPose(reason: RefusalReason, phase: RefusalAnimationPhase): CareReactionPose {
  if (phase === 'cue') return { fxX: 0, fxY: -10, bodyX: 0, bodyY: 0, angle: 0, scale: 1 };
  if (reason === 'tantrum') return { fxX: 0, fxY: -12, bodyX: -4, bodyY: 1, angle: -7, scale: 1.04 };
  if (reason === 'cooldown') return { fxX: 7, fxY: -9, bodyX: -1, bodyY: 0, angle: -2, scale: 0.98 };
  return { fxX: -7, fxY: -8, bodyX: 2, bodyY: 0, angle: 3, scale: 0.98 };
}

export function refusalEffectPixels(severity: 'mild' | 'tantrum'): ColoredPixelPoint[] {
  const dark = severity === 'tantrum' ? 0x7a1230 : 0x4a2a14;
  const red = severity === 'tantrum' ? 0xff315c : 0xe63b45;
  const light = severity === 'tantrum' ? 0xffb8c8 : 0xffd1c8;
  const pixels: ColoredPixelPoint[] = [];
  const line = (fromX: number, toX: number, y: number, color: number): void => {
    pixels.push(...rangePixels(fromX, toX, y, color));
  };
  const vertical = (x: number, fromY: number, toY: number, color: number): void => {
    for (let y = fromY; y <= toY; y += 1) pixels.push({ x, y, color });
  };

  line(-2, 2, -18, dark); line(-4, 4, -17, dark); line(-5, 5, -16, dark);
  vertical(-6, -15, -11, dark); vertical(6, -15, -11, dark);
  line(-5, 5, -10, dark); line(-4, 4, -9, dark); line(-2, 2, -8, dark);
  line(-2, 2, -17, red); line(-4, 4, -16, red);
  vertical(-5, -15, -11, red); vertical(5, -15, -11, red);
  line(-4, 4, -10, red); line(-2, 2, -9, red);
  for (let i = 0; i < 7; i += 1) pixels.push({ x: -3 + i, y: -11 - i, color: red });
  for (let i = 0; i < 5; i += 1) pixels.push({ x: -2 + i, y: -11 - i, color: light });
  return pixels;
}

export function poopNeedIconPixels(): ColoredPixelPoint[] {
  const outline = 0x4a2412;
  const shadow = 0x5c2b12;
  const mid = 0x9b521f;
  const light = 0xe2a34e;
  return [
    ...rangePixels(-6, 6, -7, outline),
    ...rangePixels(-6, 6, -8, outline),
    ...rangePixels(-5, 5, -9, outline),
    ...rangePixels(-4, 4, -10, outline),
    ...rangePixels(-4, 4, -11, outline),
    ...rangePixels(-3, 3, -12, outline),
    ...rangePixels(-2, 3, -13, outline),
    ...rangePixels(-1, 3, -14, outline),
    ...rangePixels(0, 3, -15, outline),
    { x: 2, y: -16, color: outline },
    { x: -5, y: -8, color: mid },
    ...rangePixels(-4, 4, -8, mid),
    { x: 5, y: -8, color: shadow },
    ...rangePixels(-4, 4, -9, mid),
    ...rangePixels(-3, 3, -10, mid),
    ...rangePixels(-3, 3, -11, mid),
    ...rangePixels(-2, 2, -12, mid),
    ...rangePixels(-1, 2, -13, mid),
    ...rangePixels(0, 2, -14, mid),
    ...rangePixels(1, 2, -15, mid),
    ...rangePixels(-4, 3, -9, light),
    ...rangePixels(-2, 2, -12, light),
    { x: 0, y: -13, color: light },
    { x: 1, y: -14, color: light },
    ...rangePixels(-3, 2, -10, shadow),
    { x: 2, y: -10, color: shadow },
    { x: -3, y: -11, color: shadow },
    { x: 2, y: -11, color: shadow },
  ];
}

function rangePixels(fromX: number, toX: number, y: number, color: number): ColoredPixelPoint[] {
  const pixels: ColoredPixelPoint[] = [];
  for (let x = fromX; x <= toX; x += 1) pixels.push({ x, y, color });
  return pixels;
}

export function buildCreatureRenderer(
  scene: Phaser.Scene,
  initial: Creature,
  opts: BuildOpts = {},
): CreatureRendererHandle {
  const reducedMotion = opts.reducedMotion ?? false;
  const showLabels = opts.showLabels ?? true;
  const sleepMode = opts.sleepMode ?? 'clock';
  const expressionOverride = opts.expressionOverride;
  const poseOverride = opts.poseOverride;
  const w = scene.scale.width;
  const h = scene.scale.height;

  // Posiciona logo acima do chão da casa (74% da altura) ao invés do centro
  const floorY = Math.floor(h * 0.74);
  const container = scene.add.container(w / 2, floorY - 60).setDepth(20);
  // PIXEL_SCALE inteiro garante pixel-perfect; o opts.scale aplica multiplicador adicional
  const finalScale = Math.max(1, Math.round((opts.scale ?? 1) * PIXEL_SCALE));
  container.setScale(finalScale);

  // Centraliza o canvas lógico no container (origem em 0,0 do container = centro)
  const ox = -CANVAS / 2;
  const oy = -CANVAS / 2;

  // SHADOW separada — fica no chão sem hop. Container fora do hop tween.
  const shadowContainer = scene.add.container(container.x, container.y).setDepth(19).setScale(finalScale);
  const shadow = scene.add.graphics();
  shadow.setAlpha(0.24);
  const shadowPx = new PixelCanvas(shadow);
  shadowContainer.add(shadow);

  // BODY no container que faz hop
  const presentationLayer = scene.add.container(0, 0);
  container.add(presentationLayer);
  const body = scene.add.graphics();
  const bodyPx = new PixelCanvas(body);
  presentationLayer.add(body);

  const fx = scene.add.graphics();
  const fxPx = new PixelCanvas(fx);
  presentationLayer.add(fx);

  // Sync shadow para sempre seguir container.x e y atual (sem o tween do body)
  const syncShadow = (): void => {
    shadowContainer.setPosition(container.x, container.y);
  };

  // Labels FORA do container (em scene-space, fonte readable em px reais)
  const labelOffset = (CANVAS / 2) * finalScale;
  const stageOffset = labelOffset + 14;
  const label = scene.add
    .text(container.x, container.y + labelOffset, initial.name, {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#dddddd',
      stroke: '#000',
      strokeThickness: 3,
      resolution: 2,
    })
    .setOrigin(0.5)
    .setDepth(200)
    .setVisible(showLabels);
  const stageText = scene.add
    .text(container.x, container.y + stageOffset, '', {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#bbbbbb',
      stroke: '#000',
      strokeThickness: 2,
      resolution: 2,
    })
    .setOrigin(0.5)
    .setDepth(200)
    .setVisible(showLabels);
  const sleepMarks = Array.from({ length: 3 }, (_, i) => {
    const mark = scene.add.graphics().setDepth(201).setVisible(false);
    drawSleepZ(mark, i);
    return mark;
  });
  const sleepPhase = { value: 0 };

  // Sync labels to container position via scene event
  const syncLabels = (): void => {
    label.setPosition(container.x, container.y + labelOffset);
    stageText.setPosition(container.x, container.y + stageOffset);
    const sleeping = isCurrentSleeping();
    sleepMarks.forEach((mark, i) => {
      const t = (sleepPhase.value + i * 0.24) % 1;
      const alpha = t < 0.15 ? t / 0.15 : t > 0.82 ? (1 - t) / 0.18 : 1;
      mark
        .setVisible(showLabels && sleeping)
        .setPosition(container.x + 42 + t * 36, container.y - 18 - t * 62)
        .setScale(3.15 + i * 0.22 + t * 0.3)
        .setAlpha(sleeping ? Phaser.Math.Clamp(alpha, 0, 1) : 0);
    });
    syncShadow();
  };
  scene.events.on(Phaser.Scenes.Events.POST_UPDATE, syncLabels);

  const setStagePosition = (x: number, y: number): void => {
    container.setPosition(x, y);
    syncLabels();
  };

  const setFacing = (direction: -1 | 1): void => {
    container.setScale(finalScale * direction, finalScale);
  };

  let current = initial;
  let currentLightOn = opts.lightOn ?? true;
  let blinking = false;
  let reacting = false;
  let reactionToken = 0;
  let sleepTween: Phaser.Tweens.Tween | undefined;
  let sleepMarkTween: Phaser.Tweens.Tween | undefined;
  let fxTween: Phaser.Tweens.Tween | undefined;
  let lastSleeping = false;

  const currentTemperament = (): TemperamentProfile => deriveTemperament(current.genome);

  const creatureAlpha = (): number => (currentLightOn ? 1 : 0.62);

  const applySceneLight = (): void => {
    body.setAlpha(creatureAlpha());
    shadow.setAlpha(currentLightOn ? 0.24 : 0.16);
  };

  const isCurrentSleeping = (): boolean => {
    return resolveRendererSleeping(current.lifecycle.stage, Date.now(), sleepMode);
  };

  const setSleepMarksVisible = (visible: boolean): void => {
    sleepMarks.forEach((mark) => mark.setVisible(showLabels && visible));
  };

  const applySleepPose = (): void => {
    const bodyType = current.genome.body;
    body.angle = 0;
    if (bodyType === 'fish') {
      body.x = -1;
      body.y = 4.4;
      body.scaleX = 1.08;
      body.scaleY = 0.72;
      return;
    }
    if (bodyType === 'ghost') {
      body.x = 0;
      body.y = 5.2;
      body.scaleX = 1.14;
      body.scaleY = 0.7;
      return;
    }
    if (bodyType === 'tall') {
      body.x = 0;
      body.y = 6;
      body.scaleX = 1.32;
      body.scaleY = 0.62;
      return;
    }
    body.x = 0;
    body.y = 5.4;
    body.scaleX = 1.2;
    body.scaleY = 0.68;
  };

  const applyPoseOverride = (): void => {
    if (!poseOverride) return;
    const pose = idleBreathPose(poseOverride === 'breathe-in' ? 'inhale' : 'exhale');
    body.angle = 0;
    body.x = 0;
    body.y = pose.y;
    body.scaleX = pose.scaleX;
    body.scaleY = pose.scaleY;
  };

  const startSleepMarks = (): void => {
    sleepMarkTween?.stop();
    sleepPhase.value = 0;
    sleepMarkTween = scene.tweens.add({
      targets: sleepPhase,
      value: 1,
      duration: 2200,
      ease: 'Linear',
      repeat: -1,
    });
  };

  // ===== IDLE/WALK ANIMATION =====
  let idleTween: Phaser.Tweens.Tween | undefined;
  let walkTween: Phaser.Tweens.Tween | undefined;
  let walking = false;
  let eggTimer: Phaser.Time.TimerEvent | undefined;
  let blinkTimer: Phaser.Time.TimerEvent | undefined;

  const startIdle = (): void => {
    stopIdle();
    const stage = current.lifecycle.stage;
    if (stage === 'dead') return;
    if (walking) return;
    const sleeping = isCurrentSleeping();
    setSleepMarksVisible(sleeping);
    if (sleeping) {
      applySleepPose();
      startSleepMarks();
      if (reducedMotion) return;
      sleepTween = scene.tweens.add({
        targets: body,
        y: body.y - 0.8,
        scaleY: body.scaleY + 0.04,
        duration: 1500,
        ease: 'Sine.InOut',
        yoyo: true,
        repeat: -1,
      });
      return;
    }
    if (reducedMotion) return;
    if (stage === 'egg') {
      body.angle = 0;
      body.y = 0;
      body.scaleY = 1;
      // Ovo fica paradinho; ocasionalmente "respira"/abaixa um pouquinho e volta.
      scheduleEggBreath();
      return;
    }
    // Respiração ancorada: squash/stretch sutil sem tirar a base do chão.
    const inhale = idleBreathPose('inhale');
    const exhale = idleBreathPose('exhale');
    body.angle = 0;
    idleTween = scene.tweens.add({
      targets: body,
      y: { from: exhale.y, to: inhale.y },
      scaleX: { from: exhale.scaleX, to: inhale.scaleX },
      scaleY: { from: exhale.scaleY, to: inhale.scaleY },
      duration: idleDurationForTemperament(currentTemperament()),
      ease: 'Sine.InOut',
      yoyo: true,
      repeat: -1,
    });
  };

  const stopWalk = (): void => {
    walkTween?.stop();
    walkTween = undefined;
    body.x = 0;
    body.y = 0;
    body.scaleX = 1;
    body.scaleY = 1;
  };

  const startWalk = (): void => {
    stopWalk();
    const stage = current.lifecycle.stage;
    if (reducedMotion || stage === 'egg' || stage === 'dead' || isCurrentSleeping()) return;
    const left = walkMotionPose('left');
    const right = walkMotionPose('right');
    walkTween = scene.tweens.add({
      targets: body,
      x: { from: left.x, to: right.x },
      y: { from: left.y, to: right.y },
      scaleX: { from: left.scaleX, to: right.scaleX },
      scaleY: { from: left.scaleY, to: right.scaleY },
      duration: 360,
      ease: 'Sine.InOut',
      yoyo: true,
      repeat: -1,
    });
  };

  const setWalking = (nextWalking: boolean): void => {
    if (walking === nextWalking) return;
    walking = nextWalking;
    if (nextWalking) {
      stopIdle();
      startWalk();
      return;
    }
    stopWalk();
    startIdle();
  };

  const scheduleEggBreath = (): void => {
    eggTimer = scene.time.addEvent({
      delay: 2200 + Math.random() * 2600,
      callback: () => {
        if (current.lifecycle.stage !== 'egg') return;
        eggBreath();
        scheduleEggBreath();
      },
    });
  };

  // Movimento natural: respiração/abaixadinha sutil ancorada na base.
  // Squash leve com anticipação, sem rotação (rotação faz parecer flutuar).
  const eggBreath = (): void => {
    eventBus.emit('creature.egg-wiggle', { creatureId: current.id });
    scene.tweens.chain({
      targets: body,
      tweens: [
        // pequena esticadinha (inspira)
        { scaleY: 1.04, scaleX: 0.98, y: -0.6, duration: 360, ease: 'Sine.InOut' },
        // abaixa comprimindo (expira), base fica fixa no chão
        { scaleY: 0.9, scaleX: 1.05, y: 1.6, duration: 320, ease: 'Sine.InOut', hold: 180 },
        // volta ao normal
        { scaleY: 1, scaleX: 1, y: 0, duration: 380, ease: 'Sine.InOut' },
      ],
      onComplete: () => {
        body.scaleX = 1;
        body.scaleY = 1;
        body.y = 0;
      },
    });
  };

  const stopIdle = (): void => {
    idleTween?.stop();
    idleTween = undefined;
    sleepTween?.stop();
    sleepTween = undefined;
    sleepMarkTween?.stop();
    sleepMarkTween = undefined;
    sleepPhase.value = 0;
    eggTimer?.remove();
    eggTimer = undefined;
    scene.tweens.killTweensOf(body);
    scene.tweens.killTweensOf(sleepPhase);
    body.x = 0;
    body.y = 0;
    body.alpha = creatureAlpha();
    body.angle = 0;
    body.scaleX = 1;
    body.scaleY = 1;
    sleepMarks.forEach((mark) => mark.setVisible(false));
  };

  const beginReaction = (): number => {
    reactionToken += 1;
    reacting = true;
    opts.onReactionStart?.();
    walking = false;
    stopWalk();
    stopIdle();
    clearFx();
    return reactionToken;
  };

  const finishReaction = (token: number): void => {
    if (token !== reactionToken) return;
    body.x = 0;
    body.y = 0;
    body.alpha = creatureAlpha();
    body.angle = 0;
    body.scaleX = 1;
    body.scaleY = 1;
    clearFx();
    reacting = false;
    startIdle();
    opts.onReactionEnd?.();
  };

  const clearFx = (): void => {
    fxTween?.stop();
    fxTween = undefined;
    fxPx.clear();
    fx.x = 0;
    fx.y = 0;
    fx.alpha = 1;
    fx.scaleX = 1;
    fx.scaleY = 1;
    fx.angle = 0;
  };

  const drawFoodFx = (foodId: FoodId): void => {
    clearFx();
    foodEffectPixels(foodId).forEach((pixel) => fxPx.px(pixel.x, pixel.y, pixel.color, pixel.alpha ?? 1));
  };

  const drawPlayFx = (toyId: ToyId): void => {
    clearFx();
    toyEffectPixels(toyId).forEach((pixel) => fxPx.px(pixel.x, pixel.y, pixel.color, pixel.alpha ?? 1));
  };

  const drawCleanFx = (phase: 'tool' | 'sparkles' = 'tool'): void => {
    clearFx();
    if (phase === 'tool') {
      cleaningEffectPixels().forEach((pixel) => fxPx.px(pixel.x, pixel.y, pixel.color, pixel.alpha ?? 1));
      return;
    }
    for (const [x, y, c] of [
      [-9, -2, 0xd8fbff],
      [-4, -6, 0x9cf6ff],
      [5, -5, 0xd8fbff],
      [9, 1, 0x9cf6ff],
      [0, 5, 0xffffff],
    ] as Array<[number, number, number]>) {
      fxPx.rect(x - 1, y - 1, 3, 3, c, 0.82);
      fxPx.px(x, y - 2, 0xffffff, 0.7);
    }
  };

  const drawHealFx = (phase: 'syringe' | 'recovery' = 'syringe'): void => {
    clearFx();
    medicineEffectPixels(phase).forEach((pixel) => fxPx.px(pixel.x, pixel.y, pixel.color, pixel.alpha ?? 1));
  };

  const drawDisciplineFx = (correct: boolean): void => {
    clearFx();
    disciplineEffectPixels(correct).forEach((pixel) => fxPx.px(pixel.x, pixel.y, pixel.color, pixel.alpha ?? 1));
  };

  const drawNeedFx = (kind: 'feed' | 'play' | 'clean' | 'heal'): void => {
    clearFx();
    if (kind === 'feed') {
      fxPx.rect(-4, -10, 8, 5, 0xf0c870);
      fxPx.hLine(-3, -11, 6, 0xfff0b8);
      fxPx.px(-5, -8, 0x8a6428);
      fxPx.px(4, -8, 0x8a6428);
      fxPx.px(0, -14, 0xfff0b8);
      return;
    }
    if (kind === 'play') {
      fxPx.px(0, -13, 0xff6eb4);
      fxPx.hLine(-1, -12, 3, 0xff6eb4);
      fxPx.hLine(-2, -11, 5, 0xff6eb4);
      fxPx.hLine(-1, -10, 3, 0xd94f88);
      fxPx.px(0, -9, 0xd94f88);
      return;
    }
    if (kind === 'heal') {
      fxPx.hLine(-4, -11, 9, 0x66ff99);
      fxPx.vLine(0, -15, 9, 0x66ff99);
      fxPx.hLine(-2, -11, 5, 0xffffff, 0.7);
      fxPx.vLine(0, -13, 5, 0xffffff, 0.7);
      return;
    }
    poopNeedIconPixels().forEach((p) => fxPx.px(p.x, p.y, p.color, p.alpha ?? 1));
    fxPx.px(-1, -15, 0x9cf6ff);
    fxPx.px(2, -16, 0xd8fbff);
  };

  const drawRefuseFx = (severity: 'mild' | 'tantrum'): void => {
    clearFx();
    refusalEffectPixels(severity).forEach((pixel) => fxPx.px(pixel.x, pixel.y, pixel.color, pixel.alpha ?? 1));
  };

  // ===== PISCAR =====
  const scheduleBlink = (): void => {
    if (reducedMotion) return;
    blinkTimer = scene.time.addEvent({
      delay: 2500 + Math.random() * 3000,
      callback: () => {
        const stage = current.lifecycle.stage;
        if (stage !== 'egg' && stage !== 'dead' && !reacting) {
          blinking = true;
          draw(current);
          scene.time.delayedCall(140, () => {
            blinking = false;
            draw(current);
          });
        }
        scheduleBlink();
      },
    });
  };

  let hopTween: Phaser.Tweens.Tween | undefined;
  void hopTween;

  const draw = (c: Creature): void => {
    bodyPx.clear();
    shadowPx.clear();
    const sleeping = resolveRendererSleeping(c.lifecycle.stage, Date.now(), sleepMode);
    setSleepMarksVisible(sleeping);
    const visualProfile = deriveVisualEvolutionProfile(c);
    const baseExpr = statsToExpression(c.state, c.lifecycle.stage, sleeping);
    let expr = baseExpr;
    if (blinking) expr = 'blink';
    if (!blinking && stageAllowsDeepVisualEvolution(c.lifecycle.stage)) {
      expr = expressionForVisualBias(expr, visualProfile);
    }
    if (expressionOverride) expr = expressionOverride;
    const colors = paletteFromHue(c.genome.paletteHue);
    const stage = c.lifecycle.stage;

    if (stage === 'egg') {
      drawCreatureShadow(shadowPx, shadowProfileForCreature(c.genome.body, 0, visualProfile, false));
      // Rachadura conforme se aproxima da eclosão (últimos 30% do tempo de ovo)
      const ageMs = Date.now() - c.lifecycle.bornAt;
      const eggDur = EGG_DURATION_MS;
      const prog = eggDur > 0 ? ageMs / eggDur : 0;
      const crack = prog > 0.85 ? 2 : prog > 0.65 ? 1 : 0;
      drawEgg(bodyPx, ox, oy, colors, c.genome.eggPattern, crack);
      label.setText(c.name);
      stageText.setText('ovo');
      setSleepMarksVisible(false);
      shadow.setVisible(true);
      applySceneLight();
      return;
    }

    if (stage === 'dead') {
      drawDead(bodyPx, ox, oy);
      label.setText(c.name);
      stageText.setText('faleceu');
      setSleepMarksVisible(false);
      shadow.setVisible(false);
      applySceneLight();
      return;
    }

    const sizeStage = stage === 'baby' ? 0 : stage === 'teen' ? 1 : 2;
    drawCreatureShadow(shadowPx, shadowProfileForCreature(c.genome.body, sizeStage, visualProfile, sleeping));
    const baseDeformAmplitude = resolveDeformAmplitudeForCreature(c);
    const deformAmplitude = stageAllowsDeepVisualEvolution(c.lifecycle.stage)
      ? applyVisualProfileDeform(baseDeformAmplitude, visualProfile)
      : baseDeformAmplitude;
    drawTail(bodyPx, ox, oy, c.genome.tail, c.genome.body, colors, sizeStage, visualProfile);
    drawBody(bodyPx, ox, oy, c.genome.body, colors, sizeStage, c.state.sick, deformAmplitude);
    drawLimbs(bodyPx, ox, oy, c.genome.body, colors, sizeStage, visualProfile);
    drawMarkings(bodyPx, ox, oy, c.genome.markings, c.genome.body, colors, sizeStage);
    if (stage !== 'baby') {
      drawEars(bodyPx, ox, oy, c.genome.ears, colors, sizeStage, c.genome.body);
      drawHorns(bodyPx, ox, oy, c.genome.horns, colors, sizeStage, c.genome.body);
    }
    drawFace(bodyPx, ox, oy, c.genome.body, expr, sizeStage, mouthExpressionForBlink(expr, baseExpr));
    drawCareTraitAccents(bodyPx, ox, oy, c);
    applyPoseOverride();

    label.setText(c.name);
    stageText.setText(
      `${sleeping ? 'sleep ' : ''}${stage}${c.state.tantrum ? ' · birra' : ''}${c.state.sick ? ' · sick' : ''}`,
    );
    shadow.setVisible(true);
    applySceneLight();
  };

  let lastStage = initial.lifecycle.stage;
  draw(current);
  lastSleeping = isCurrentSleeping();
  startIdle();
  scheduleBlink();

  return {
    container,
    setWorldDepth(depth: number): void {
      const actorDepth = Number.isFinite(depth) ? depth : 0;
      container.setDepth(actorDepth);
      shadowContainer.setDepth(actorDepth - 0.5);
    },
    setFacing,
    setWalking,
    setStagePosition,
    setLightOn(lightOn: boolean): void {
      currentLightOn = lightOn;
      applySceneLight();
    },
    setPresentationPose(pose: Partial<PresentationPose>): void {
      const normalized = normalizePresentationPose(pose);
      presentationLayer
        .setPosition(normalized.x, normalized.y)
        .setAngle(normalized.angle)
        .setScale(normalized.scaleX, normalized.scaleY);
    },
    resetPresentationPose(): void {
      presentationLayer.setPosition(0, 0).setAngle(0).setScale(1, 1);
    },
    update(c: Creature): void {
      current = c;
      const sleepingNow = isCurrentSleeping();
      draw(c);
      if (c.lifecycle.stage !== lastStage || sleepingNow !== lastSleeping) {
        const wasEgg = lastStage === 'egg';
        lastStage = c.lifecycle.stage;
        lastSleeping = sleepingNow;
        if (wasEgg && c.lifecycle.stage === 'baby') {
          eventBus.emit('creature.hatched', { creatureId: c.id });
        }
        startIdle();
      }
    },
    reactFeed(foodId: FoodId = 'meal'): void {
      const token = beginReaction();
      drawFoodFx(foodId);
      const present = feedingAnimationPose('present');
      fx.setPosition(present.foodX, present.foodY).setScale(present.foodScale);
      scene.tweens.add({
        targets: fx,
        x: feedingAnimationPose('approach').foodX,
        y: feedingAnimationPose('approach').foodY,
        duration: 220,
        ease: 'Quad.InOut',
        onComplete: () => {
          if (token !== reactionToken) return;
          scene.tweens.add({
            targets: body,
            y: feedingAnimationPose('bite-one').bodyY,
            scaleY: 0.94,
            duration: 130,
            yoyo: true,
          });
          scene.tweens.add({
            targets: fx,
            scaleX: feedingAnimationPose('bite-one').foodScale,
            scaleY: feedingAnimationPose('bite-one').foodScale,
            y: feedingAnimationPose('bite-one').foodY,
            duration: 130,
            onComplete: () => {
              if (token !== reactionToken) return;
              scene.tweens.add({
                targets: body,
                y: feedingAnimationPose('bite-two').bodyY,
                scaleY: 0.94,
                duration: 110,
                yoyo: true,
              });
              scene.tweens.add({
                targets: fx,
                x: feedingAnimationPose('bite-two').foodX,
                y: feedingAnimationPose('bite-two').foodY,
                scaleX: feedingAnimationPose('bite-two').foodScale,
                scaleY: feedingAnimationPose('bite-two').foodScale,
                alpha: 0,
                duration: 170,
                ease: 'Quad.In',
                onComplete: () => finishReaction(token),
              });
            },
          });
        },
      });
    },
    reactPlay(toyId: ToyId = 'ball'): void {
      const token = beginReaction();
      drawPlayFx(toyId);
      const start = toyMotionPose(toyId, 'present');
      const interact = toyMotionPose(toyId, 'interact');
      const finish = toyMotionPose(toyId, 'finish');
      fx.setPosition(start.fxX, start.fxY).setAngle(start.angle).setScale(start.scale);
      scene.tweens.add({ targets: body, y: interact.bodyY, duration: 180, yoyo: true, repeat: 1, ease: 'Sine.InOut' });
      scene.tweens.add({
        targets: fx, x: interact.fxX, y: interact.fxY, angle: interact.angle,
        scaleX: interact.scale, scaleY: interact.scale, duration: 420,
        yoyo: toyId === 'ball' || toyId === 'rope', repeat: toyId === 'rope' ? 1 : 0,
        ease: toyId === 'ball' ? 'Bounce.Out' : 'Sine.InOut',
        onComplete: () => {
          if (token !== reactionToken) return;
          scene.tweens.add({
            targets: fx, x: finish.fxX, y: finish.fxY, scaleX: finish.scale, scaleY: finish.scale,
            alpha: 0, duration: 180, onComplete: () => finishReaction(token),
          });
        },
      });
    },
    reactClean(): void {
      const token = beginReaction();
      drawCleanFx('tool');
      const present = cleaningAnimationPose('present');
      const sweep = cleaningAnimationPose('sweep');
      const finish = cleaningAnimationPose('finish');
      fx.setPosition(present.fxX, present.fxY).setAngle(present.angle).setScale(present.scale);
      scene.tweens.add({
        targets: fx,
        x: sweep.fxX,
        y: sweep.fxY,
        angle: sweep.angle,
        duration: 190,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.InOut',
        onComplete: () => {
          if (token !== reactionToken) return;
          drawCleanFx('sparkles');
          fx.setPosition(finish.fxX, finish.fxY).setAngle(0).setScale(finish.scale).setAlpha(0.3);
          scene.tweens.add({
            targets: fx,
            x: 0,
            y: -3,
            alpha: 1,
            scaleX: 1.15,
            scaleY: 1.15,
            duration: 210,
            yoyo: true,
            hold: 100,
            onComplete: () => finishReaction(token),
          });
        },
      });
      scene.tweens.add({
        targets: body,
        x: sweep.bodyX,
        y: sweep.bodyY,
        duration: 190,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.InOut',
      });
    },
    reactHeal(): void {
      const token = beginReaction();
      drawHealFx('syringe');
      const present = medicineAnimationPose('present');
      const inject = medicineAnimationPose('inject');
      const recover = medicineAnimationPose('recover');
      fx.setPosition(present.fxX, present.fxY).setAngle(present.angle).setScale(present.scale);
      scene.tweens.add({
        targets: fx,
        x: inject.fxX,
        y: inject.fxY,
        angle: inject.angle,
        duration: 230,
        ease: 'Quad.InOut',
        onComplete: () => {
          if (token !== reactionToken) return;
          scene.tweens.add({ targets: body, x: inject.bodyX, y: inject.bodyY, duration: 70, yoyo: true, repeat: 1 });
          scene.time.delayedCall(180, () => {
            if (token !== reactionToken) return;
            drawHealFx('recovery');
            fx.setPosition(recover.fxX, recover.fxY).setAngle(0).setScale(healFxScaleForTemperament(currentTemperament()));
            scene.tweens.add({
              targets: [body, fx],
              scaleX: recover.scale,
              scaleY: recover.scale,
              alpha: 0.9,
              duration: 210,
              yoyo: true,
              hold: 80,
              ease: 'Sine.InOut',
              onComplete: () => finishReaction(token),
            });
          });
        },
      });
    },
    reactNeed(kind: 'feed' | 'play' | 'clean' | 'heal'): void {
      if (reacting) return;
      clearFx();
      drawNeedFx(kind);
      fx.y = -2;
      fx.alpha = 0;
      let tween: Phaser.Tweens.Tween;
      tween = scene.tweens.add({
        targets: fx,
        y: -8,
        alpha: 1,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 180,
        yoyo: true,
        hold: 520,
        ease: 'Sine.InOut',
        onComplete: () => {
          if (fxTween !== tween) return;
          clearFx();
        },
      });
      fxTween = tween;
    },
    reactRefuse(severity: 'mild' | 'tantrum' = 'mild', context: RefusalReactionContext = {}): void {
      const token = beginReaction();
      const hard = severity === 'tantrum';
      const reason = context.reason ?? (hard ? 'tantrum' : 'not-needed');
      const pose = refusalAnimationPose(reason, 'react');
      drawRefuseFx(severity);
      fx.setPosition(0, 0).setScale(1);
      scene.tweens.add({
        targets: body,
        x: pose.bodyX,
        y: pose.bodyY,
        angle: pose.angle,
        duration: hard ? 60 : 70,
        yoyo: true,
        repeat: refuseRepeatsForTemperament(currentTemperament(), severity),
        onComplete: () => finishReaction(token),
      });
    },
    reactDiscipline(correct = true): void {
      const token = beginReaction();
      const cue = disciplineAnimationPose(correct, 'cue');
      const resolve = disciplineAnimationPose(correct, 'resolve');
      drawDisciplineFx(correct);
      fx.setPosition(cue.fxX, cue.fxY).setScale(cue.scale);
      if (correct) scene.cameras.main.shake(120, 0.002);
      scene.tweens.add({
        targets: body,
        x: resolve.bodyX,
        y: resolve.bodyY,
        angle: resolve.angle,
        scaleX: resolve.scale,
        scaleY: resolve.scale,
        duration: 170,
        yoyo: true,
        hold: correct ? 100 : 0,
        ease: correct ? 'Sine.Out' : 'Back.Out',
        onComplete: () => finishReaction(token),
      });
    },
    reactEvolve(tier: CareTier): void {
      const token = beginReaction();
      const [fr, fg, fb] =
        tier === 'bom' || tier === 'secreto'
          ? [200, 255, 200]
          : tier === 'medio'
            ? [220, 220, 220]
            : [200, 60, 60];
      scene.cameras.main.flash(300, fr, fg, fb);
      scene.tweens.add({
        targets: body,
        scaleX: 1.25,
        scaleY: 1.25,
        duration: 260,
        yoyo: true,
        ease: 'Back.Out',
        onComplete: () => finishReaction(token),
      });
    },
    destroy(): void {
      scene.events.off(Phaser.Scenes.Events.POST_UPDATE, syncLabels);
      stopIdle();
      clearFx();
      blinkTimer?.remove();
      hopTween?.stop();
      label.destroy();
      stageText.destroy();
      sleepMarks.forEach((mark) => mark.destroy());
      shadowContainer.destroy();
      container.destroy();
    },
  };
}

// ============================================================================
// PIXEL ART DRAW HELPERS — coordenadas relativas ao topo-esquerdo do canvas 32×32
// ============================================================================

interface BodyShape {
  // Cada linha do bitmap: array de [start, len] em coluna (relativo ao centro horizontal cx)
  rows: Array<[number, number]>;
  // y inicial absoluto (topo), e cy lógico p/ centro
  topY: number;
  cy: number;
}

/** Bitmap silhueta: colunas onde cada linha tem fillRect — eficiente para pixel art. */
function drawSleepZ(g: Phaser.GameObjects.Graphics, variant: number): void {
  g.clear();
  const zPixels: Array<[number, number]> = [
    [0, 0], [1, 0], [2, 0], [3, 0], [4, 0],
    [3, 1],
    [2, 2],
    [1, 3],
    [0, 4], [1, 4], [2, 4], [3, 4], [4, 4],
  ];
  const dark = 0x31548f;
  const light = variant === 0 ? 0xdff4ff : variant === 1 ? 0xffffff : 0xbfe8ff;
  const offset = -2;
  const hasPixel = (x: number, y: number): boolean => zPixels.some(([px, py]) => px === x && py === y);

  g.fillStyle(dark, 0.95);
  for (const [x, y] of zPixels) {
    for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as Array<[number, number]>) {
      const nx = x + dx;
      const ny = y + dy;
      if (!hasPixel(nx, ny)) g.fillRect(offset + nx, offset + ny, 1, 1);
    }
  }

  g.fillStyle(light, 1);
  for (const [x, y] of zPixels) {
    g.fillRect(offset + x, offset + y, 1, 1);
  }
}

/** Meia-largura real da silhueta na linha yRel (relativa a cy); 0 se a linha não existe no corpo. */
function rowHalfWidth(shape: BodyShape, yRel: number): number {
  const idx = yRel - shape.topY;
  if (idx < 0 || idx >= shape.rows.length) return 0;
  const [start, len] = shape.rows[idx]!;
  return Math.max(Math.abs(start), Math.abs(start + len - 1));
}

/** Como Math.round, mas nunca colapsa um input não-zero para 0 (preserva previews sutis de amplitude fracionária). */
function roundNonZero(x: number): number {
  if (x === 0) return 0;
  const r = Math.round(x);
  return r !== 0 ? r : Math.sign(x);
}

/**
 * Deforma a silhueta (mesma família de corpo) por amplitude de cuidado.
 * amplitude < 0 = ramo ruim (postura caída, barriga inchada embaixo);
 * amplitude > 0 = ramo bom (cintura definida, postura ereta); 0 = neutro.
 */
export function applyCareDeformation(shape: BodyShape, amplitude: number): BodyShape {
  if (amplitude === 0) return shape;
  const n = shape.rows.length;
  const rows: Array<[number, number]> = shape.rows.map(([start, len], i) => {
    const t = n <= 1 ? 0 : i / (n - 1); // 0 = topo, 1 = base
    const lean = amplitude < 0 ? roundNonZero(-amplitude * t * 5) : 0;
    let widthDelta = 0;
    if (amplitude > 0 && t > 0.3 && t < 0.65) widthDelta = -roundNonZero(amplitude * 2); // cintura
    if (amplitude < 0 && t > 0.65) widthDelta = roundNonZero(-amplitude * 3); // barriga/base inchada
    if (amplitude < 0 && t < 0.2) widthDelta -= roundNonZero(-amplitude); // ombros caídos
    const newLen = Math.max(1, len + widthDelta * 2);
    const newStart = start - widthDelta + lean;
    return [newStart, newLen];
  });
  return { ...shape, rows };
}

function drawCareTraitAccents(px: PixelCanvas, ox: number, oy: number, c: Creature): void {
  const accents = new Set(deriveVisualCareAccents(c));
  if (accents.size === 0 && c.lifecycle.careTier !== 'secreto') return;
  const stage = c.lifecycle.stage === 'baby' ? 0 : c.lifecycle.stage === 'teen' ? 1 : 2;
  const isElder = c.lifecycle.stage === 'elder';
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2 + anchorCy(c.genome.body, stage);
  const body = c.genome.body;
  const palette = paletteFromHue(c.genome.paletteHue);
  const shape = shapeFor(body, stage);
  const bottomYRel = shape.topY + shape.rows.length - 1;

  if (accents.has('soft-belly')) {
    const bellyY = cy + (body === 'fish' ? 2 : stage === 0 ? 4 : 6);
    const bellyW = body === 'tall' ? 5 : body === 'fish' ? 6 : stage === 2 ? 9 : 7;
    px.hLine(cx - Math.floor(bellyW / 2), bellyY, bellyW, blendColor(palette.light, 0xffffff, 0.35), 0.55);
    px.hLine(cx - Math.floor(bellyW / 2) + 1, bellyY + 1, Math.max(2, bellyW - 2), palette.midLight, 0.45);
  }

  if (accents.has('cheek-blush')) {
    const yRel = 3;
    const half = rowHalfWidth(shape, yRel);
    px.px(cx - half - 1, cy + yRel, 0xffb6c1, 0.65);
    px.px(cx + half + 1, cy + yRel, 0xffb6c1, 0.65);
  }

  if (accents.has('indulgent-crown')) {
    const y = cy + shape.topY - 1;
    px.px(cx - 1, y, 0xffd166);
    px.px(cx, y - 1, 0xfff08a);
    px.px(cx + 1, y, 0xffd166);
    px.px(cx - 2, y - 1, 0xfff08a, 0.75);
    px.px(cx + 2, y - 1, 0xfff08a, 0.75);
  }

  if (accents.has('play-spark')) {
    const yRel = -2;
    const half = rowHalfWidth(shape, yRel);
    const ex = cx + half + 2;
    const ey = cy + yRel;
    px.px(ex, ey, 0xfff08a);
    px.px(ex - 1, ey + 1, 0xfff08a);
    px.px(ex + 1, ey + 1, 0xfff08a);
    px.px(ex, ey + 2, 0xfff08a);
  }

  if (accents.has('mess-smudge')) {
    const yRel = bottomYRel - 1;
    const half = rowHalfWidth(shape, yRel);
    px.px(cx - half - 2, cy + yRel, 0x6fa34f, 0.8);
    px.px(cx - half - 3, cy + yRel - 2, 0x8fd66f, 0.65);
    px.px(cx + half + 2, cy + yRel + 1, 0x6fa34f, 0.75);
  }

  if (accents.has('discipline-band')) {
    const yRel = 1;
    const half = rowHalfWidth(shape, yRel);
    const w = Math.max(3, half * 2 - 1);
    px.hLine(cx - Math.floor(w / 2), cy + yRel, w, 0x7aa7ff, 0.72);
    px.px(cx, cy + yRel + 1, 0xe8f0ff, 0.7);
  } else if (accents.has('rebel-spikes')) {
    const yRel = -5;
    const half = rowHalfWidth(shape, yRel);
    px.px(cx - half - 2, cy + yRel, 0xff5555, 0.85);
    px.px(cx - half - 1, cy + yRel - 1, 0xff5555, 0.85);
    px.px(cx + half + 2, cy + yRel, 0xff5555, 0.85);
  }

  if (accents.has('recovery-spark')) {
    const yRel = shape.topY + 2;
    const half = rowHalfWidth(shape, yRel);
    const ex = cx - half - 2;
    const ey = cy + yRel;
    px.px(ex, ey, 0x66ff99, 0.9);
    px.px(ex - 1, ey + 1, 0x66ff99, 0.65);
    px.px(ex + 1, ey + 1, 0x66ff99, 0.65);
  }

  if (accents.has('frail-haze')) {
    px.hLine(cx - 3, cy + shape.topY - 2, 7, 0xb8d7ff, 0.45);
  }

  if (isElder && accents.has('recovery-spark')) {
    px.px(cx + 2, cy + shape.topY - 2, 0xc8f7ff, 0.7);
  }

  if (c.lifecycle.careTier === 'secreto') {
    px.px(cx, cy + shape.topY - 3, 0xfff4b0, 0.95);
    px.px(cx - 1, cy + shape.topY - 2, 0xfff4b0, 0.7);
    px.px(cx + 1, cy + shape.topY - 2, 0xfff4b0, 0.7);
  }
}

function drawCreatureShadow(px: PixelCanvas, shadow: CreatureShadowProfile): void {
  const cy = CANVAS / 2 + shadow.offsetY;
  drawShadowEllipse(px, shadow.offsetX, cy, shadow.rx + 2, shadow.ry + 1, shadow.alpha * 0.34);
  drawShadowEllipse(px, shadow.offsetX, cy, shadow.rx, shadow.ry, shadow.alpha);
  if (shadow.ry <= 1) {
    px.hLine(shadow.offsetX - Math.max(2, shadow.rx - 2), cy, Math.max(5, shadow.rx * 2 - 3), 0x000000, shadow.alpha * 0.8);
  }
}

function drawShadowEllipse(px: PixelCanvas, cx: number, cy: number, rx: number, ry: number, alpha: number): void {
  for (let y = -ry; y <= ry; y++) {
    const rowRatio = ry === 0 ? 0 : (y * y) / (ry * ry);
    const w = Math.round(rx * Math.sqrt(Math.max(0, 1 - rowRatio)));
    if (w > 0) px.hLine(cx - w, cy + y, w * 2 + 1, 0x000000, alpha);
  }
}

function shapeRound(stage: 0 | 1 | 2): BodyShape {
  // 0=baby (raio 5), 1=teen (raio 7), 2=adult (raio 9)
  const r = [5, 7, 9][stage]!;
  const rows: Array<[number, number]> = [];
  for (let y = -r; y <= r; y++) {
    const half = Math.round(Math.sqrt(r * r - y * y));
    rows.push([-half, half * 2 + 1]);
  }
  return { rows, topY: -r, cy: 0 };
}

function shapeTall(stage: 0 | 1 | 2): BodyShape {
  const ry = [6, 8, 10][stage]!;
  const rx = [4, 5, 6][stage]!;
  const rows: Array<[number, number]> = [];
  for (let y = -ry; y <= ry; y++) {
    const half = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry))));
    rows.push([-half, half * 2 + 1]);
  }
  return { rows, topY: -ry, cy: 0 };
}

function shapeBlob(stage: 0 | 1 | 2): BodyShape {
  const rx = [7, 9, 11][stage]!;
  const ry = [4, 5, 7][stage]!;
  const rows: Array<[number, number]> = [];
  for (let y = -ry; y <= ry; y++) {
    const half = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry))));
    rows.push([-half, half * 2 + 1]);
  }
  return { rows, topY: -ry, cy: 0 };
}

function shapeFish(stage: 0 | 1 | 2): BodyShape {
  // Corpo principal (elipse) — cauda fica desenhada à parte
  const rx = [7, 9, 11][stage]!;
  const ry = [3, 4, 5][stage]!;
  const rows: Array<[number, number]> = [];
  for (let y = -ry; y <= ry; y++) {
    const half = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry))));
    rows.push([-half, half * 2 + 1]);
  }
  return { rows, topY: -ry, cy: 0 };
}

function shapeGhost(stage: 0 | 1 | 2): BodyShape {
  // Topo arredondado + base ondulada; aqui aproximamos com retângulo+arco
  const w = [9, 11, 13][stage]!;
  const headR = [4, 5, 6][stage]!;
  const trunk = [3, 5, 6][stage]!;
  const rows: Array<[number, number]> = [];
  // arco superior
  for (let y = -headR; y <= 0; y++) {
    const half = Math.round(headR * Math.sqrt(Math.max(0, 1 - (y * y) / (headR * headR))));
    rows.push([-half, half * 2 + 1]);
  }
  // tronco retangular
  for (let y = 1; y <= trunk; y++) {
    const wobble = y === trunk ? 1 : y % 2 === 0 ? 0 : -1;
    const half = Math.max(3, Math.floor(w / 2) + wobble);
    rows.push([-half, half * 2 + 1]);
  }
  return { rows, topY: -headR, cy: 0 };
}

function shapeFor(body: BodyGene, stage: 0 | 1 | 2): BodyShape {
  switch (body) {
    case 'tall':
      return shapeTall(stage);
    case 'blob':
      return shapeBlob(stage);
    case 'fish':
      return shapeFish(stage);
    case 'ghost':
      return shapeGhost(stage);
    case 'round':
    default:
      return shapeRound(stage);
  }
}

/** Offset vertical pra ancorar a base do bicho perto da sombra (y≈13). */
const BASE_ANCHOR_Y = 13;
function anchorCy(body: BodyGene, stage: 0 | 1 | 2): number {
  const shape = shapeFor(body, stage);
  const bottom = shape.topY + (shape.rows.length - 1); // relativo a cy
  // fish: corpo flutua um pouco mais alto (nada no ar)
  const target = body === 'fish' ? BASE_ANCHOR_Y - 3 : BASE_ANCHOR_Y;
  return target - bottom;
}

function drawBody(
  px: PixelCanvas,
  ox: number,
  oy: number,
  body: BodyGene,
  c: ReturnType<typeof paletteFromHue>,
  stage: 0 | 1 | 2,
  sick: boolean,
  deformAmplitude: number,
): void {
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2 + anchorCy(body, stage);

  const baseColor = sick ? blendColor(c.base, 0x88ff88, 0.5) : c.base;
  const midColor = sick ? blendColor(c.midLight, 0xaaffaa, 0.5) : c.midLight;
  const lightColor = sick ? blendColor(c.light, 0xccffcc, 0.5) : c.light;
  const darkColor = c.dark;

  const shape = applyCareDeformation(shapeFor(body, stage), deformAmplitude);

  // Preenche silhueta com gradient vertical (3 tons): topo claro, meio base, base escura
  const totalRows = shape.rows.length;
  for (let i = 0; i < totalRows; i++) {
    const row = shape.rows[i]!;
    const yRel = shape.topY + i;
    const y = cy + yRel;
    const t = i / Math.max(1, totalRows - 1);
    let rowColor: number;
    if (t < 0.25) rowColor = lightColor;
    else if (t < 0.7) rowColor = baseColor;
    else rowColor = midColor;
    px.hLine(cx + row[0], y, row[1], rowColor);
  }

  // Outline 1px (top/bottom + lados) - apenas onde há transição com fora
  for (let i = 0; i < totalRows; i++) {
    const row = shape.rows[i]!;
    const y = cy + shape.topY + i;
    const left = cx + row[0];
    const right = cx + row[0] + row[1] - 1;
    px.px(left - 0, y, darkColor);
    // (não escreve outline quando linha tem 1px só pra evitar borrar)
    if (row[1] > 1) px.px(right, y, darkColor);
    // outline topo/base
    if (i === 0) {
      for (let x = 0; x < row[1]; x++) px.px(cx + row[0] + x, y, darkColor);
    }
    if (i === totalRows - 1) {
      for (let x = 0; x < row[1]; x++) px.px(cx + row[0] + x, y, darkColor);
    }
  }

  // Highlight dot (brilho)
  const hRow = shape.rows[Math.max(0, Math.floor(totalRows * 0.18))]!;
  const hx = cx + hRow[0] + Math.max(1, Math.floor(hRow[1] * 0.25));
  const hy = cy + shape.topY + Math.floor(totalRows * 0.18);
  px.px(hx, hy, 0xffffff, 0.9);
  px.px(hx + 1, hy, c.glare, 0.6);
  px.px(hx, hy + 1, c.glare, 0.6);

  // Pés para round/blob (2 patinhas pequenas)
  if (body === 'round' || body === 'blob') {
    const lastRow = shape.rows[totalRows - 1]!;
    const footY = cy + shape.topY + totalRows;
    const footL = cx + lastRow[0] + 1;
    const footR = cx + lastRow[0] + lastRow[1] - 2;
    px.rect(footL - 1, footY, 3, 2, darkColor);
    px.rect(footR - 1, footY, 3, 2, darkColor);
  }

  // Cauda fish — murcha e desce quando o ramo é ruim (deformAmplitude < 0)
  if (body === 'fish') {
    const droop = Math.max(0, -deformAmplitude);
    const tailW = Math.max(2, [3, 4, 5][stage]! - Math.round(droop));
    const tailH = Math.max(2, [4, 5, 7][stage]! - Math.round(droop * 2));
    const tailX = cx + shape.rows[Math.floor(totalRows / 2)]![1] / 2 + 6;
    const tailY = cy - Math.floor(tailH / 2) + Math.round(droop * 3);
    for (let y = 0; y < tailH; y++) {
      const w = Math.max(1, Math.round(tailW * (1 - Math.abs(y - tailH / 2) / (tailH / 2))));
      px.hLine(tailX, tailY + y, w, baseColor);
    }
    // outline da cauda
    for (let y = 0; y < tailH; y++) {
      const w = Math.max(1, Math.round(tailW * (1 - Math.abs(y - tailH / 2) / (tailH / 2))));
      px.px(tailX + w - 1, tailY + y, darkColor);
    }
  }

  // Ondulação inferior fantasma
  if (body === 'ghost') {
    const lastRow = shape.rows[totalRows - 1]!;
    const wavY = cy + shape.topY + totalRows;
    for (let i = 0; i < lastRow[1]; i++) {
      const x = cx + lastRow[0] + i;
      if (i % 4 === 0) px.px(x, wavY + 1, darkColor);
      else if (i % 2 === 0) px.px(x, wavY, baseColor);
      else px.px(x, wavY, darkColor);
    }
  }
}

function drawTail(
  px: PixelCanvas,
  ox: number,
  oy: number,
  tail: TailGene,
  body: BodyGene,
  c: ReturnType<typeof paletteFromHue>,
  stage: 0 | 1 | 2,
  visualProfile: VisualEvolutionProfile,
): void {
  if (tail === 'none' || body === 'ghost') return;
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2 + anchorCy(body, stage);
  const shape = shapeFor(body, stage);
  const attachIndex =
    body === 'fish' ? Math.floor(shape.rows.length / 2) : Math.floor(shape.rows.length * 0.86);
  const attachRow = shape.rows[attachIndex]!;
  const right = cx + attachRow[0] + attachRow[1] - 1;
  const y = cy + shape.topY + attachIndex;
  const baseGrowth = stage === 0 ? 0 : stage === 1 ? 1 : 2;
  const growth = tailGrowthForVisualStyle(baseGrowth, visualProfile.limbStyle);
  const base = c.base;
  const dark = c.dark;

  switch (tail) {
    case 'stub':
      px.rect(right - 1, y, 4 + growth, 3, base);
      px.px(right + 2 + growth, y, dark);
      px.px(right + 2 + growth, y + 2, dark);
      break;
    case 'curl':
      px.hLine(right - 1, y, 5 + growth, base);
      px.px(right + 3 + growth, y - 1, base);
      px.px(right + 3 + growth, y + 1, base);
      px.px(right + 2 + growth, y + 2, dark);
      px.px(right + 1 + growth, y + 2, dark);
      if (stage === 2) px.px(right + 2, y - 2, dark);
      break;
    case 'fin':
      if (body !== 'fish') {
        px.rect(right - 1, y, 3 + growth, 2, base);
        px.px(right + 1 + growth, y, dark);
        px.px(right + 1 + growth, y + 1, dark);
        break;
      }
      for (let i = 0; i < 5 + growth; i++) {
        const center = (4 + growth) / 2;
        const len = Math.max(1, Math.round((3 + growth) * (1 - Math.abs(i - center) / center)));
        px.hLine(right, y - 2 + i, len + 1, c.light);
        px.px(right + len, y - 2 + i, dark);
      }
      break;
  }
}

function drawLimbs(
  px: PixelCanvas,
  ox: number,
  oy: number,
  body: BodyGene,
  c: ReturnType<typeof paletteFromHue>,
  stage: 0 | 1 | 2,
  visualProfile: VisualEvolutionProfile,
): void {
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2 + anchorCy(body, stage);
  const shape = shapeFor(body, stage);
  const midRow = shape.rows[Math.floor(shape.rows.length * 0.58)]!;
  const left = cx + midRow[0];
  const right = cx + midRow[0] + midRow[1] - 1;
  const y = cy + 1;
  const base = c.base;
  const dark = c.dark;

  if (body === 'fish') {
    const finExtra = visualProfile.limbStyle === 'fins' ? 1 : 0;
    px.px(cx - 1, cy + 2, c.light);
    px.px(cx, cy + 3 + finExtra, c.light);
    if (stage > 0) px.px(cx + 1 + finExtra, cy + 2, dark);
    return;
  }
  if (body === 'ghost') {
    if (stage === 0) return;
    px.hLine(left - 2, y, 3, base);
    px.px(left - 2, y + 1, dark);
    px.hLine(right, y, 3, base);
    px.px(right + 2, y + 1, dark);
    return;
  }
  if (stage === 0) return;

  const baseArmLen = stage === 1 ? 2 : 3;
  const armLen = armLengthForVisualStyle(baseArmLen, visualProfile.limbStyle);
  px.hLine(left - armLen, y, armLen + 1, base);
  px.px(left - armLen, y + 1, dark);
  px.hLine(right, y, armLen + 1, base);
  px.px(right + armLen, y + 1, dark);
}

function drawMarkings(
  px: PixelCanvas,
  ox: number,
  oy: number,
  markings: MarkingGene,
  body: BodyGene,
  c: ReturnType<typeof paletteFromHue>,
  stage: 0 | 1 | 2,
): void {
  if (markings === 'none') return;
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2 + anchorCy(body, stage);
  const mark = blendColor(c.dark, c.base, 0.35);
  const blush = 0xff8fa3;
  const eyeY = cy - 3;

  switch (markings) {
    case 'cheeks':
      px.px(cx - 5, eyeY + 5, blush, 0.7);
      px.px(cx + 5, eyeY + 5, blush, 0.7);
      if (stage > 0) {
        px.px(cx - 6, eyeY + 5, blush, 0.45);
        px.px(cx + 6, eyeY + 5, blush, 0.45);
      }
      break;
    case 'freckles':
      px.px(cx - 5, eyeY + 4, mark, 0.75);
      px.px(cx - 3, eyeY + 5, mark, 0.65);
      px.px(cx + 4, eyeY + 4, mark, 0.75);
      px.px(cx + 6, eyeY + 5, mark, 0.65);
      break;
    case 'stripes': {
      const width = stage === 0 ? 5 : stage === 1 ? 7 : 9;
      px.hLine(cx - Math.floor(width / 2), cy - 6, width, mark, 0.55);
      px.hLine(cx - Math.floor(width / 2) - 1, cy - 3, width + 2, mark, 0.45);
      if (body !== 'tall') px.hLine(cx - Math.floor(width / 2), cy, width, mark, 0.35);
      break;
    }
    case 'mask':
      px.hLine(cx - 5, eyeY - 1, 4, mark, 0.75);
      px.hLine(cx + 2, eyeY - 1, 4, mark, 0.75);
      px.px(cx - 1, eyeY, mark, 0.45);
      px.px(cx + 1, eyeY, mark, 0.45);
      break;
  }
}

function drawHorns(
  px: PixelCanvas,
  ox: number,
  oy: number,
  horns: HornGene,
  c: ReturnType<typeof paletteFromHue>,
  stage: 0 | 1 | 2,
  body: BodyGene,
): void {
  if (horns === 'none') return;
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2 + anchorCy(body, stage);
  const shape = shapeFor(body, stage);
  const topY = cy + shape.topY;
  const horn = 0xfff0b8;
  const shade = blendColor(c.dark, horn, 0.35);
  const h = stage === 0 ? 2 : stage === 1 ? 3 : 4;

  switch (horns) {
    case 'nub':
      px.rect(cx - 3, topY - 2, 2, 2, horn);
      px.rect(cx + 2, topY - 2, 2, 2, horn);
      px.px(cx - 3, topY, shade);
      px.px(cx + 3, topY, shade);
      break;
    case 'twin':
      for (let i = 0; i < h; i++) {
        px.px(cx - 4 + Math.floor(i / 2), topY - i, horn);
        px.px(cx + 4 - Math.floor(i / 2), topY - i, horn);
      }
      px.px(cx - 4, topY, shade);
      px.px(cx + 4, topY, shade);
      break;
    case 'crown':
      px.hLine(cx - 4, topY, 9, shade);
      px.vLine(cx - 3, topY - h + 1, h, horn);
      px.vLine(cx, topY - h, h + 1, horn);
      px.vLine(cx + 3, topY - h + 1, h, horn);
      px.px(cx - 3, topY - h, horn);
      px.px(cx, topY - h - 1, horn);
      px.px(cx + 3, topY - h, horn);
      break;
  }
}

function drawEars(
  px: PixelCanvas,
  ox: number,
  oy: number,
  earType: EarGene,
  c: ReturnType<typeof paletteFromHue>,
  stage: 0 | 1 | 2,
  body: BodyGene,
): void {
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2 + anchorCy(body, stage);
  const shape = shapeFor(body, stage);
  const offX = stage === 1 ? 5 : stage === 0 ? 4 : 7;
  // ancora as orelhas no topo real do corpo
  const baseY = cy + shape.topY + 1;
  const dark = c.dark;
  const base = c.base;
  const lx = cx - offX;
  const rx = cx + offX;
  switch (earType) {
    case 'round':
      px.rect(lx - 1, baseY - 2, 3, 3, base);
      px.rect(rx - 1, baseY - 2, 3, 3, base);
      // outline
      px.px(lx - 1, baseY - 2, dark);
      px.px(lx + 1, baseY - 2, dark);
      px.px(lx - 1, baseY, dark);
      px.px(lx + 1, baseY, dark);
      px.px(rx - 1, baseY - 2, dark);
      px.px(rx + 1, baseY - 2, dark);
      px.px(rx - 1, baseY, dark);
      px.px(rx + 1, baseY, dark);
      break;
    case 'pointy':
      // triângulos 3 px alto
      px.px(lx, baseY, base);
      px.hLine(lx - 1, baseY - 1, 2, base);
      px.px(lx - 1, baseY - 2, base);
      px.px(rx, baseY, base);
      px.hLine(rx, baseY - 1, 2, base);
      px.px(rx + 1, baseY - 2, base);
      // inner pink
      px.px(lx, baseY - 1, c.light);
      px.px(rx + 1, baseY - 1, c.light);
      break;
    case 'floppy':
      px.rect(lx - 2, baseY, 4, 2, base);
      px.rect(rx - 1, baseY, 4, 2, base);
      px.px(lx - 2, baseY + 1, dark);
      px.px(lx + 1, baseY + 1, dark);
      px.px(rx - 1, baseY + 1, dark);
      px.px(rx + 2, baseY + 1, dark);
      break;
    case 'antenna':
      if (stage === 2) {
        px.vLine(lx, baseY - 3, 3, dark);
        px.hLine(lx - 2, baseY - 1, 3, dark);
        px.px(lx - 2, baseY - 2, c.light);
        px.vLine(rx, baseY - 3, 3, dark);
        px.hLine(rx, baseY - 1, 3, dark);
        px.px(rx + 2, baseY - 2, c.light);
        break;
      }
      px.vLine(lx, baseY - 3, 3, dark);
      px.vLine(rx, baseY - 3, 3, dark);
      px.px(lx, baseY - 4, c.light);
      px.px(rx, baseY - 4, c.light);
      px.px(lx - 1, baseY - 4, c.light);
      px.px(rx + 1, baseY - 4, c.light);
      break;
  }
}

function drawFace(
  px: PixelCanvas,
  ox: number,
  oy: number,
  body: BodyGene,
  expr: Expression,
  stage: 0 | 1 | 2,
  mouthExpr: Expression = expr,
): void {
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2 + anchorCy(body, stage) - 1;
  if (body === 'fish') {
    drawFishFace(px, cx, cy, expr, stage, mouthExpr);
    return;
  }
  const eyeOff = eyeOffsetForFace(body, stage);
  const eyeY = stage === 0 ? cy - 2 : cy - 2;
  const lex = cx - eyeOff;
  const rex = cx + eyeOff;
  const black = 0x111111;
  const white = 0xffffff;
  switch (expr) {
    case 'happy':
      // ^^
      px.px(lex - 1, eyeY, black);
      px.px(lex + 1, eyeY, black);
      px.px(lex, eyeY - 1, black);
      px.px(rex - 1, eyeY, black);
      px.px(rex + 1, eyeY, black);
      px.px(rex, eyeY - 1, black);
      // smile
      px.hLine(cx - 2, eyeY + 3, 5, black);
      px.px(cx - 3, eyeY + 2, black);
      px.px(cx + 3, eyeY + 2, black);
      // bochecha
      px.px(cx - 4, eyeY + 1, 0xff8888, 0.7);
      px.px(cx + 4, eyeY + 1, 0xff8888, 0.7);
      break;
    case 'sad':
      px.rect(lex, eyeY, 2, 2, black);
      px.rect(rex - 1, eyeY, 2, 2, black);
      px.px(lex, eyeY, white, 0.65);
      px.px(rex - 1, eyeY, white, 0.65);
      // boca pra baixo
      px.hLine(cx - 1, eyeY + 3, 3, black);
      px.px(cx - 2, eyeY + 4, black);
      px.px(cx + 2, eyeY + 4, black);
      // lágrima
      px.px(lex - 1, eyeY + 1, 0x99ddff, 0.95);
      px.px(lex - 1, eyeY + 2, 0x55bbff, 0.9);
      break;
    case 'sick':
      // Glassy small eyes with under-eye color read as unwell, not bored.
      px.rect(lex - 1, eyeY, 2, 2, black);
      px.rect(rex, eyeY, 2, 2, black);
      px.px(lex - 1, eyeY, white, 0.65);
      px.px(rex, eyeY, white, 0.65);
      px.hLine(lex - 1, eyeY + 2, 2, 0x66bbff, 0.5);
      px.hLine(rex, eyeY + 2, 2, 0x66bbff, 0.5);
      // boca ondulada de enjoo
      px.px(cx - 2, eyeY + 4, black);
      px.px(cx - 1, eyeY + 3, black);
      px.px(cx, eyeY + 4, black);
      px.px(cx + 1, eyeY + 3, black);
      px.px(cx + 2, eyeY + 4, black);
      break;
    case 'blink':
      // _ _
      px.hLine(lex - 1, eyeY, 3, black);
      px.hLine(rex - 1, eyeY, 3, black);
      drawFaceMouth(px, cx, eyeY, mouthExpr);
      break;
    case 'sleepy':
      px.hLine(lex - 1, eyeY, 3, black);
      px.hLine(rex - 1, eyeY, 3, black);
      break;
    case 'angry':
      px.rect(lex, eyeY, 2, 2, black);
      px.rect(rex - 1, eyeY, 2, 2, black);
      // sobrancelha bravas \  /
      px.px(lex - 2, eyeY - 2, black);
      px.px(lex - 1, eyeY - 1, black);
      px.px(lex, eyeY - 1, black);
      px.px(rex + 2, eyeY - 2, black);
      px.px(rex + 1, eyeY - 1, black);
      px.px(rex, eyeY - 1, black);
      // boca tensa
      px.hLine(cx - 1, eyeY + 3, 3, black);
      break;
    case 'neutral':
    default:
      // olhos grandes redondos com brilho (fofo)
      px.rect(lex - 1, eyeY - 1, 3, 3, black);
      px.rect(rex - 1, eyeY - 1, 3, 3, black);
      px.px(lex, eyeY - 1, white);
      px.px(rex, eyeY - 1, white);
      // boca pequena fofa
      px.px(cx, eyeY + 3, black);
      px.px(cx - 1, eyeY + 4, black);
      px.px(cx + 1, eyeY + 4, black);
      // bochechas rosa
      px.px(cx - 4, eyeY + 3, 0xff9aa8, 0.6);
      px.px(cx + 4, eyeY + 3, 0xff9aa8, 0.6);
      break;
  }
}

function drawFaceMouth(px: PixelCanvas, cx: number, eyeY: number, expr: Expression): void {
  const black = 0x111111;
  switch (expr) {
    case 'happy':
      px.hLine(cx - 2, eyeY + 3, 5, black);
      px.px(cx - 3, eyeY + 2, black);
      px.px(cx + 3, eyeY + 2, black);
      break;
    case 'sad':
      px.hLine(cx - 1, eyeY + 3, 3, black);
      px.px(cx - 2, eyeY + 4, black);
      px.px(cx + 2, eyeY + 4, black);
      break;
    case 'sick':
      px.px(cx - 2, eyeY + 4, black);
      px.px(cx - 1, eyeY + 3, black);
      px.px(cx, eyeY + 4, black);
      px.px(cx + 1, eyeY + 3, black);
      px.px(cx + 2, eyeY + 4, black);
      break;
    case 'angry':
      px.hLine(cx - 1, eyeY + 3, 3, black);
      break;
    case 'sleepy':
      break;
    case 'blink':
    case 'neutral':
    default:
      px.px(cx, eyeY + 3, black);
      px.px(cx - 1, eyeY + 4, black);
      px.px(cx + 1, eyeY + 4, black);
      break;
  }
}

function drawFishFace(
  px: PixelCanvas,
  cx: number,
  cy: number,
  expr: Expression,
  stage: 0 | 1 | 2,
  mouthExpr: Expression = expr,
): void {
  const black = 0x111111;
  const white = 0xffffff;
  const eyeX = cx - (stage === 0 ? 3 : 4);
  const eyeY = cy - 1;
  const mouth = fishMouthAnchor(cx, cy, stage);

  switch (expr) {
    case 'happy':
      px.rect(eyeX, eyeY, 2, 2, black);
      px.px(eyeX, eyeY, white);
      drawFishMouth(px, mouth.x, mouth.y, mouthExpr);
      break;
    case 'sad':
      px.rect(eyeX, eyeY, 2, 2, black);
      px.px(eyeX, eyeY, white, 0.65);
      px.px(eyeX - 1, eyeY + 2, 0x55bbff, 0.9);
      drawFishMouth(px, mouth.x, mouth.y, mouthExpr);
      break;
    case 'sick':
      px.rect(eyeX, eyeY, 2, 2, black);
      px.px(eyeX, eyeY, white, 0.65);
      px.hLine(eyeX, eyeY + 2, 2, 0x66bbff, 0.5);
      drawFishMouth(px, mouth.x, mouth.y, mouthExpr);
      break;
    case 'blink':
      px.hLine(eyeX - 1, eyeY, 3, black);
      drawFishMouth(px, mouth.x, mouth.y, mouthExpr);
      break;
    case 'sleepy':
      px.hLine(eyeX - 1, eyeY, 3, black);
      break;
    case 'angry':
      px.rect(eyeX, eyeY, 2, 2, black);
      px.px(eyeX, eyeY, white, 0.55);
      px.px(eyeX + 1, eyeY - 1, black);
      px.px(eyeX + 2, eyeY - 2, black);
      drawFishMouth(px, mouth.x, mouth.y, mouthExpr);
      break;
    case 'neutral':
    default:
      px.rect(eyeX, eyeY, 2, 2, black);
      px.px(eyeX, eyeY, white);
      drawFishMouth(px, mouth.x, mouth.y, mouthExpr);
      break;
  }
}

function drawFishMouth(px: PixelCanvas, mouthX: number, mouthY: number, expr: Expression): void {
  const black = 0x111111;
  fishMouthPixels(expr, mouthX, mouthY).forEach((p) => px.px(p.x, p.y, black));
}

function drawDead(px: PixelCanvas, ox: number, oy: number): void {
  const cx = ox + CANVAS / 2;
  const cy = oy + CANVAS / 2;
  const grey = 0x6a6a6a;
  const dark = 0x3a3a3a;
  // corpo cinza
  for (let y = -7; y <= 7; y++) {
    const half = Math.round(7 * Math.sqrt(Math.max(0, 1 - (y * y) / 49)));
    if (half > 0) px.hLine(cx - half, cy + y, half * 2 + 1, grey);
  }
  // X X olhos
  px.px(cx - 4, cy - 3, dark);
  px.px(cx - 3, cy - 2, dark);
  px.px(cx - 2, cy - 1, dark);
  px.px(cx - 4, cy - 1, dark);
  px.px(cx - 2, cy - 3, dark);
  px.px(cx + 4, cy - 3, dark);
  px.px(cx + 3, cy - 2, dark);
  px.px(cx + 2, cy - 1, dark);
  px.px(cx + 4, cy - 1, dark);
  px.px(cx + 2, cy - 3, dark);
}

function drawEgg(
  px: PixelCanvas,
  ox: number,
  oy: number,
  c: ReturnType<typeof paletteFromHue>,
  pattern: string,
  crackLevel = 0, // 0 = intacto, 1 = rachadura inicial, 2 = rachado
): void {
  const cx = ox + CANVAS / 2;
  // Ovo proporcional clássico: topo arredondado (não pontudo), curva gradual e simétrica
  // na largura, base bojuda arredondada. 20 linhas, largura máx 8.
  const halves = [3, 4, 5, 6, 6, 7, 7, 7, 8, 8, 8, 8, 8, 8, 7, 7, 6, 6, 5, 3];
  const rows = halves.length;
  // Base levantada do chão (respiro com a sombra).
  const bottomY = oy + CANVAS / 2 + 12;
  const cyTop = bottomY - (rows - 1);

  const halfAt = (i: number): number => (i >= 0 && i < rows ? halves[i]! : 0);

  // Preenche com gradiente vertical de 3 tons (luz em cima)
  for (let i = 0; i < rows; i++) {
    const half = halfAt(i);
    if (half <= 0) continue;
    const y = cyTop + i;
    const t = i / (rows - 1);
    let col: number;
    if (t < 0.3) col = c.light;
    else if (t < 0.7) col = c.base;
    else col = c.midLight;
    px.hLine(cx - half, y, half * 2 + 1, col);
  }

  // Contorno fechado
  for (let i = 0; i < rows; i++) {
    const half = halfAt(i);
    if (half <= 0) continue;
    const y = cyTop + i;
    const prev = halfAt(i - 1);
    px.px(cx - half, y, c.dark);
    px.px(cx + half, y, c.dark);
    if (prev === 0) {
      px.hLine(cx - half, y, half * 2 + 1, c.dark);
    } else if (half > prev + 1) {
      for (let x = prev + 1; x <= half; x++) {
        px.px(cx - x, cyTop + i - 1, c.dark);
        px.px(cx + x, cyTop + i - 1, c.dark);
      }
    }
  }
  px.hLine(cx - halfAt(rows - 1), bottomY, halfAt(rows - 1) * 2 + 1, c.dark);

  // padrões
  const midY = cyTop + Math.floor(rows / 2);
  if (pattern === 'spots' || pattern === 'dots') {
    px.rect(cx - 3, midY - 2, 2, 2, c.dark);
    px.rect(cx + 1, midY + 2, 2, 2, c.dark);
    px.px(cx - 1, midY + 4, c.dark);
  } else if (pattern === 'stripes') {
    px.hLine(cx - 6, midY - 2, 13, c.dark);
    px.hLine(cx - 7, midY + 2, 15, c.dark);
  } else if (pattern === 'patches') {
    px.rect(cx - 4, midY - 2, 3, 2, c.dark);
    px.rect(cx + 1, midY + 2, 3, 2, c.dark);
  } else if (pattern === 'stars') {
    px.px(cx, midY - 2, 0xfff7c0);
    px.px(cx - 1, midY - 1, 0xfff7c0);
    px.px(cx + 1, midY - 1, 0xfff7c0);
    px.px(cx, midY, 0xfff7c0);
    px.px(cx, midY + 4, 0xfff7c0);
  } else if (pattern === 'checks') {
    for (let yy = -3; yy <= 5; yy += 2)
      for (let xx = -4; xx <= 4; xx += 2)
        if ((xx + yy) % 4 === 0) px.px(cx + xx, midY + yy, c.dark);
  } else if (pattern === 'dark') {
    for (let i = 1; i < rows - 1; i++) {
      const half = halfAt(i) - 1;
      if (half > 0) px.hLine(cx - half, cyTop + i, half * 2 + 1, blendColor(c.base, 0x000000, 0.4));
    }
  }

  // Rachadura (perto de eclodir) — zig-zag no topo
  if (crackLevel >= 1) {
    const cr = 0x2a1a0a;
    px.px(cx, cyTop + 1, cr);
    px.px(cx - 1, cyTop + 2, cr);
    px.px(cx + 1, cyTop + 2, cr);
    px.px(cx, cyTop + 3, cr);
  }
  if (crackLevel >= 2) {
    const cr = 0x2a1a0a;
    px.px(cx + 2, cyTop + 3, cr);
    px.px(cx + 1, cyTop + 4, cr);
    px.px(cx - 2, cyTop + 4, cr);
    px.px(cx - 1, cyTop + 5, cr);
    px.px(cx + 2, cyTop + 5, cr);
  }

  // brilho
  px.px(cx - 2, cyTop + 3, 0xffffff, 0.9);
  px.px(cx - 1, cyTop + 3, 0xffffff, 0.5);
  px.px(cx - 2, cyTop + 4, 0xffffff, 0.45);
}

function blendColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar * (1 - t) + br * t);
  const g = Math.round(ag * (1 - t) + bg * t);
  const bl = Math.round(ab * (1 - t) + bb * t);
  return (r << 16) | (g << 8) | bl;
}
